class ProductDiscountCountdown extends HTMLElement {
  constructor() {
    super();
    this.onTick = this.onTick.bind(this);
  }

  connectedCallback() {
    const expiresRaw = this.getAttribute('data-expires');
    if (!expiresRaw) {
      this.hidden = true;
      return;
    }

    // Support both ISO strings and "YYYY-MM-DDTHH:MM:SS" without timezone
    this.expiresAt = new Date(expiresRaw);
    if (Number.isNaN(this.expiresAt.getTime())) {
      this.expiresAt = new Date(expiresRaw.replace(' ', 'T'));
    }

    this.prefix = this.getAttribute('data-prefix') || '';
    this.output = this.querySelector('[data-countdown-output]');

    if (!this.output || Number.isNaN(this.expiresAt.getTime())) {
      this.hidden = true;
      return;
    }

    this.onTick();
    this.timerInterval = setInterval(this.onTick, 1000);
  }

  disconnectedCallback() {
    if (this.timerInterval) clearInterval(this.timerInterval);
  }

  onTick() {
    const diff = this.expiresAt - Date.now();

    if (diff <= 0) {
      this.hidden = true;
      if (this.timerInterval) clearInterval(this.timerInterval);
      this.dispatchEvent(new CustomEvent('discount-countdown:expired', { bubbles: true }));
      return;
    }

    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const timeText = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

    this.output.textContent = days > 0
      ? `${this.prefix} ${days}d ${timeText}`.trim()
      : `${this.prefix} ${timeText}`.trim();
  }
}

class DiscountCodeCopy extends HTMLElement {
  constructor() {
    super();
    this.onCopy = this.onCopy.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('[data-copy-trigger]');
    this.button?.addEventListener('click', this.onCopy);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onCopy);
  }

  async onCopy() {
    const code = this.getAttribute('data-code');
    if (!code || !navigator.clipboard) return;

    try {
      await navigator.clipboard.writeText(code);
      const label = this.querySelector('[data-copy-label]');
      if (!label) return;

      const defaultText = label.getAttribute('data-default-text') || label.textContent;
      const copiedText = this.getAttribute('data-copied-label') || 'Copied!';

      label.textContent = copiedText;
      this.button.classList.add('is-copied');
      this.button.disabled = true;

      clearTimeout(this.resetTimeout);
      this.resetTimeout = setTimeout(() => {
        label.textContent = defaultText;
        this.button.classList.remove('is-copied');
        this.button.disabled = false;
      }, 2000);
    } catch (error) {
      console.warn(error);
    }
  }
}

class ProductDiscountFinalPrice extends HTMLElement {
  constructor() {
    super();
    this.onVariantChange = this.onVariantChange.bind(this);
  }

  connectedCallback() {
    this.finalValueEl = this.querySelector('[data-final-value]');
    this.compareValueEl = this.querySelector('[data-compare-value]');
    this.discountType = this.getAttribute('data-discount-type') || 'fixed';
    this.discountValue = parseFloat(this.getAttribute('data-discount-value')) || 0;
    this.showCompare = this.getAttribute('data-show-compare') === 'true';
    this.onVariantChangeFallback = this.onVariantChangeFallback.bind(this);

    if (theme?.pubsub?.subscribe && theme?.pubsub?.PUB_SUB_EVENTS?.variantChange) {
      this.unsubscribe = theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.variantChange,
        this.onVariantChange
      );
    } else {
      document.addEventListener('variant:change', this.onVariantChangeFallback);
    }
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    document.removeEventListener('variant:change', this.onVariantChangeFallback);
  }

  onVariantChange({ data }) {
    const variant = data?.variant;
    if (!variant || typeof variant.price !== 'number') return;
    this.updatePrice(variant.price);
  }

  onVariantChangeFallback(event) {
    const variant = event.detail?.variant;
    if (!variant || typeof variant.price !== 'number') return;
    this.updatePrice(variant.price);
  }

  get moneyFormat() {
    if (!theme?.settings) return '';
    return theme.settings.currencyCodeEnabled
      ? theme.settings.moneyWithCurrencyFormat
      : theme.settings.moneyFormat;
  }

  updatePrice(basePrice) {
    let discountCents = 0;

    if (this.discountType === 'percent') {
      discountCents = Math.round(basePrice * this.discountValue / 100);
    } else {
      discountCents = Math.round(this.discountValue * 100);
    }

    const finalPrice = Math.max(0, basePrice - discountCents);
    const format = this.moneyFormat;

    if (this.finalValueEl && theme?.Currency?.formatMoney && format) {
      this.finalValueEl.innerHTML = theme.Currency.formatMoney(finalPrice, format);
    }

    if (this.showCompare && this.compareValueEl && theme?.Currency?.formatMoney && format) {
      this.compareValueEl.innerHTML = theme.Currency.formatMoney(basePrice, format);
    }
  }
}

if (!customElements.get('product-discount-countdown')) {
  customElements.define('product-discount-countdown', ProductDiscountCountdown);
}

if (!customElements.get('discount-code-copy')) {
  customElements.define('discount-code-copy', DiscountCodeCopy);
}

if (!customElements.get('product-discount-final-price')) {
  customElements.define('product-discount-final-price', ProductDiscountFinalPrice);
}

document.addEventListener('discount-countdown:expired', (event) => {
  const coupon = event.target.closest('[data-hide-on-expire]');
  if (coupon) coupon.hidden = true;
});

document.querySelectorAll('.pdc[data-hide-on-expire] countdown-timer').forEach((timer) => {
  const observer = new MutationObserver(() => {
    if (timer.hidden) {
      const coupon = timer.closest('[data-hide-on-expire]');
      if (coupon) coupon.hidden = true;
      observer.disconnect();
    }
  });
  observer.observe(timer, { attributes: true, attributeFilter: ['hidden'] });
});
